// Counter Intelligence — in-chat item import helpers.
//
// Counter Intelligence can add brand-new items to the catalog by importing an
// uploaded file (product list, order guide, invoice, inventory export, or a
// photo). This module holds the pure pieces of that multi-step flow so they are
// unit-testable; the drawer (CounterIntelligenceDrawer.jsx) owns the I/O —
// reading files, running the menu-starter document-import workflow, the preview
// card, the DB writes, and the template-creation engine.
//
// The flow mirrors the Items page ("Import file or photo" → preview → save →
// starter templates), but stays inside the chat:
//   1. offer       — "Want me to import your product list?"  (yes/no)
//   2. upload       — the user picks one or more files
//   3. processing   — each file runs through the existing document-import engine
//   4. preview      — review the items found; remove any; save
//   5. templates    — offer to build starter count/order templates from them
//   6. summary      — recap what happened ("imported 40 items, created 2 templates")
//
// We import item LISTS only — never menus. (Menus go through a different,
// dish-oriented extractor and would be misread as items here.)
//
// Pure (no React / no I/O) so it is unit-testable.

const IMPORT_TTL_MS = 30 * 60 * 1000; // 30 minutes, same as the other CI pendings

// The file types the chat import accepts — kept in lockstep with the Items page
// upload control (add-items.html #file-input).
export const IMPORT_ACCEPT =
  '.csv,.xlsx,.xls,.txt,.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif';

// One plain-English sentence the assistant can use to answer "what files can you
// take?" — PDFs, spreadsheets, photos, basically any list of items.
export const ACCEPTED_FILES_SENTENCE =
  'I can read PDFs, Excel and CSV spreadsheets, plain-text lists, and even photos (PNG, JPG, HEIC) — a product list, an order guide, an invoice, or an export from your old system. You can also paste the rows straight into this chat. (I import item lists, not menus.)';

const AFFIRM_RE = /\b(yes|yep|yeah|yup|sure|ok|okay|please|do it|go ahead|sounds good|upload|import|let'?s do it|i do|i would|add them|create (them|it))\b/i;
const DECLINE_RE = /\b(no|not now|nope|nah|later|never ?mind|nevermind|cancel|stop|forget it|no thanks|i'?m good|all set|skip)\b/i;

const nowIso = () => new Date().toISOString();
const plusMs = (ms) => new Date(Date.now() + ms).toISOString();

export function isImportAffirmative(text) {
  const t = String(text || '');
  // A decline ("no thanks") must win even though it contains no affirm token.
  if (DECLINE_RE.test(t)) return false;
  return AFFIRM_RE.test(t);
}

export function isImportDecline(text) {
  return DECLINE_RE.test(String(text || ''));
}

// Is this reply actually asking WHAT FILES the import takes?
//
// This guards the one branch that answers with ACCEPTED_FILES_SENTENCE while
// KEEPING the offer open. It used to fire on offer_response === 'other' alone,
// and 'other' only means "a reply that isn't yes or no" — so any follow-up on
// another subject, asked while an import offer happened to be open, came back as
// the file-format list instead of being answered. That is how "how do I count
// these items" got "I can read PDFs, Excel and CSV spreadsheets…" — twice.
//
// So: require the message to name a file, a format, or the act of uploading.
// Bare "take" and "accept" are deliberately not in here — they matched ordinary
// sentences that had nothing to do with files. Anything else falls through, the
// stale offer is dropped, and the question gets classified on its own merits.
const FILE_QUESTION_RE =
  /\b(file|files|format|formats|filetype|file type|pdf|pdfs|excel|xlsx?|csv|spreadsheets?|photos?|pictures?|images?|scans?|upload|uploads|uploading|attach|attachment|order guide|price list|invoice|invoices)\b/i;

export function isAcceptedFilesQuestion(text) {
  return FILE_QUESTION_RE.test(String(text || ''));
}

// ── Step 1: the offer ────────────────────────────────────────────────────────

export function buildImportOfferPending({ text } = {}) {
  return {
    kind: 'pending_item_import',
    awaiting: 'offer',
    original_user_message: String(text || ''),
    created_at: nowIso(),
    expires_at: plusMs(IMPORT_TTL_MS),
  };
}

export function importOfferChips() {
  return [
    { key: 'import_yes', label: 'Yes, upload a file' },
    { key: 'import_no', label: 'Not now' },
  ];
}

// The short nudge the drawer shows when it offers the in-chat import. The support
// answer already explained the workflow; this is just the actionable offer.
export const IMPORT_OFFER_TEXT =
  'Want to add your items right here? Upload a product list, invoice, or order guide — or paste the rows straight into the chat — and I’ll import the items, show you a preview to save, then offer to build your count and order templates from them.';

// The offer when the user already pasted a table but their intent wasn't clear
// enough to import outright. Saying yes imports the PASTED rows (no upload).
export function pastedImportOfferText(rowCount) {
  const n = Number(rowCount) || 0;
  const what = n > 1 ? `a list of about ${n} items` : 'a list of items';
  return `That looks like ${what} with vendors and prices. Want me to import them into your Item Catalog? I’ll show you a preview to review before anything saves.`;
}

// ── Step 4 → 5: hand off to the template offer ───────────────────────────────

export function buildTemplatesPending({ session, keptItemIds } = {}) {
  return {
    kind: 'pending_item_import',
    awaiting: 'templates',
    session: session || null,
    kept_item_ids: Array.isArray(keptItemIds) ? keptItemIds : [],
    created_at: nowIso(),
    expires_at: plusMs(IMPORT_TTL_MS),
  };
}

export function templateOfferChips() {
  return [
    { key: 'tpl_yes', label: 'Yes, create templates' },
    { key: 'tpl_no', label: 'No thanks' },
  ];
}

// The Item Catalog already hands newly-added items to the conversational
// template-setup assistant on count.html. Chat imports use this exact same
// handoff so both paths get the graphical proposal/review cards, schedule
// controls, conversational edits, and graphical completion summary.
export const TEMPLATE_SETUP_HANDOFF_KEY = 'template_setup_handoff';
export const TEMPLATE_SETUP_ACTIVE_KEY_PREFIX = 'template_setup_active_';

export function templateSetupActiveKey(storeId) {
  return `${TEMPLATE_SETUP_ACTIVE_KEY_PREFIX}${Number(storeId)}`;
}

export function buildTemplateSetupHandoff({ storeId, itemIds, source, at, directive } = {}) {
  const ids = [...new Set((Array.isArray(itemIds) ? itemIds : [])
    .map(Number)
    .filter((id) => Number.isFinite(id) && id > 0))];
  const handoff = {
    store_id: Number(storeId),
    item_ids: ids,
    source: source === 'setup' ? 'setup' : 'add_items',
    at: Number.isFinite(Number(at)) ? Number(at) : Date.now(),
  };
  // A template instruction carried from the import message ("make it all into
  // one ordering template") rides only when present, so every existing caller
  // and stash stays byte-identical without one.
  const cleaned = String(directive || '').trim();
  if (cleaned) handoff.directive = cleaned.slice(0, 400);
  return handoff;
}

// ── Step 5b: existing End of Period template — add to it, or make a new one? ──
// When the store already has an End of Period template, the presumption is that
// the user wants these items added to it (one EOP usually covers everything), but
// they can still choose to spin up a second one. We ask; we don't decide for them.
export function buildEopChoicePending({ session, keptItemIds, eopExisting } = {}) {
  return {
    kind: 'pending_item_import',
    awaiting: 'eop_choice',
    session: session || null,
    kept_item_ids: Array.isArray(keptItemIds) ? keptItemIds : [],
    eop_existing: eopExisting || null,
    created_at: nowIso(),
    expires_at: plusMs(IMPORT_TTL_MS),
  };
}

export function eopChoiceChips(eopExisting) {
  const name = (eopExisting && eopExisting.name) ? String(eopExisting.name) : 'your End of Period template';
  return [
    { key: 'eop_existing', label: `Add to “${name}”` },
    { key: 'eop_new', label: 'Create a new one' },
  ];
}

// Read the user's reply to the EOP choice. "Add to"/"existing" wins first (so an
// existing template whose name contains "new" isn't misread), then an explicit
// new/second/separate; otherwise default to 'existing' (the presumption).
const EOP_EXISTING_RE = /\b(add to|existing|append|same (one|template)|keep (it|the one)|that one)\b/i;
const EOP_NEW_RE = /\b(new|create (a |another |second )?(one|template|eop)|another|second|separate|fresh|additional|extra)\b/i;
export function eopChoiceFromReply(text) {
  const t = String(text || '');
  if (EOP_EXISTING_RE.test(t)) return 'existing';
  return EOP_NEW_RE.test(t) ? 'new' : 'existing';
}

// Build the per-category choices for applyStarterCountsPlan from a plan: extend
// an existing ordering template when one already matches the category (avoids
// spawning a duplicate "Ordering — Produce"), otherwise create a new one.
export function categoryChoicesFromPlan(plan) {
  const categories = {};
  ((plan && plan.categories) || []).forEach((c) => {
    if (c && c.categoryKey != null) categories[String(c.categoryKey)] = c.existing ? 'existing' : 'new';
  });
  return categories;
}

// ── Imported-row mapping ─────────────────────────────────────────────────────
// Map a freshly-imported inventory_items row (the shape add-items.html reads back
// for review) into the compact preview row the drawer card renders.
export function mapImportedRow(row) {
  const r = row || {};
  return {
    id: r.id,
    item_name: String(r.description || r.item_name || r.name || 'Item').trim() || 'Item',
    vendor: String(r.vendor || '').trim(),
    category: String(r.category || '').trim(),
    location: String(r.location || '').trim(),
    unit: String(r.unit || '').trim(),
    pack_size: String(r.pack_size || '').trim(),
  };
}

// ── Step storage ─────────────────────────────────────────────────────────────
// The "storage for the steps": a small record that accumulates what each step of
// the import did, so the final recap can describe the whole run ("imported N
// items from X, created M templates"). It rides along on the preview card and the
// templates pending context (both persisted), so it survives page navigation.
export function newImportSession({ fileNames } = {}) {
  return {
    fileNames: Array.isArray(fileNames) ? fileNames.filter(Boolean).map(String) : [],
    startedAt: nowIso(),
    itemsImported: 0,
    templates: [], // [{ name, typeLabel }]
  };
}

// ── Template summary ─────────────────────────────────────────────────────────
// createStarterCounts returns sessions: [{ name, count, action }]. Importing
// builds MORE THAN ONE template — one End of Period template covering everything,
// plus one ordering template per category — so the summary groups them by type.
const TYPE_FROM_NAME = (name) => {
  const n = String(name || '');
  if (/end of period/i.test(n)) return 'End of Period';
  if (/ordering|order template/i.test(n)) return 'Ordering';
  return 'Template';
};

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const countWord = (n) => (n >= 0 && n < NUMBER_WORDS.length ? NUMBER_WORDS[n] : String(n));

// Pull the group name out of an "Ordering — Produce" template name.
const groupFromOrderingName = (name) => {
  const m = String(name || '').match(/[—-]\s*(.+)$/); // after an em-dash or hyphen
  return m ? m[1].trim() : '';
};

// Reduce a createStarterCounts result into the templates the recap should mention
// (only the ones actually created or extended).
export function summarizeTemplatesFromSessions(sessions) {
  const list = Array.isArray(sessions) ? sessions : [];
  return list
    .filter((s) => s && (s.action === 'created' || s.action === 'updated'))
    .map((s) => ({ name: String(s.name || 'Template').trim(), typeLabel: TYPE_FROM_NAME(s.name), action: s.action }));
}

// "an End of Period template and two ordering templates (Produce, Dairy)"
export function describeTemplates(templates) {
  const list = Array.isArray(templates) ? templates : [];
  if (!list.length) return '';
  const eop = list.filter((t) => t.typeLabel === 'End of Period');
  const ordering = list.filter((t) => t.typeLabel === 'Ordering');
  const other = list.filter((t) => t.typeLabel !== 'End of Period' && t.typeLabel !== 'Ordering');

  const parts = [];
  if (eop.length) {
    parts.push(eop.length === 1 ? 'an End of Period template' : `${countWord(eop.length)} End of Period templates`);
  }
  if (ordering.length) {
    const groups = ordering.map((t) => groupFromOrderingName(t.name)).filter(Boolean);
    const tail = groups.length ? ` (${groups.join(', ')})` : '';
    parts.push(ordering.length === 1 ? `an ordering template${tail}` : `${countWord(ordering.length)} ordering templates${tail}`);
  }
  if (other.length) {
    // A custom-named template (e.g. an existing "Monthly Inventory") — name it.
    const names = other.map((t) => `“${t.name}”`);
    parts.push(names.length === 1 ? names[0] : `${countWord(names.length)} templates (${names.join(', ')})`);
  }
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

const describeFiles = (fileNames) => {
  const names = (fileNames || []).filter(Boolean);
  if (!names.length) return '';
  if (names.length === 1) return ` from “${names[0]}”`;
  if (names.length === 2) return ` from “${names[0]}” and “${names[1]}”`;
  return ` from ${names.length} files`;
};

// The final recap. Reads the accumulated session so it reflects every step:
//   "I imported 40 items from “Sysco order guide.pdf” and created 2 templates:
//    an End of Period template and an ordering template (Produce)."
export function summarizeImport(session) {
  const s = session || {};
  const n = Number(s.itemsImported) || 0;
  const itemsPart = `I imported ${n} item${n === 1 ? '' : 's'}${describeFiles(s.fileNames)}`;
  const templates = Array.isArray(s.templates) ? s.templates : [];
  if (!templates.length) {
    return `${itemsPart}. They’re in your Item Catalog now — give them prices and vendors when you get a chance.`;
  }
  // Separate freshly-created templates from existing ones we appended to, so the
  // recap reads honestly ("created … and added them to your existing …").
  const created = templates.filter((t) => t && t.action !== 'updated');
  const updated = templates.filter((t) => t && t.action === 'updated');
  const clauses = [];
  if (created.length) {
    clauses.push(`created ${created.length} template${created.length === 1 ? '' : 's'}: ${describeTemplates(created)}`);
  }
  if (updated.length) {
    clauses.push(`added them to your existing ${describeTemplates(updated)}`);
  }
  return `${itemsPart} and ${clauses.join(' and ')}. You can count from ${(updated.length && !created.length) ? 'it' : 'them'} under Inventory → Counts.`;
}

// Lightweight pre-check before reading files (the engine's reader also validates
// and throws richer messages — this just catches the empty / oversized-batch case
// early with a friendly note).
const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.gif'];
export function validateImportFiles(fileList) {
  const files = Array.from(fileList || []);
  if (!files.length) return { ok: false, error: 'Pick at least one file to import.' };
  const imageCount = files.filter((f) => {
    const t = String(f.type || '').toLowerCase();
    const name = String(f.name || '').toLowerCase();
    return t.startsWith('image/') || IMAGE_EXTS.some((ext) => name.endsWith(ext));
  }).length;
  if (imageCount > 5) {
    return { ok: false, error: 'I can take up to 5 photos at a time. Remove a few and try again — or upload a PDF/spreadsheet instead.' };
  }
  return { ok: true };
}
